// ═══════════════════════════════════════════════════════════════
// XYQAR — Wizard Form Engine
// Renders the right form fields for the (persona, step) combo.
// All inputs glass-style on Wolf Dark surface.
// ═══════════════════════════════════════════════════════════════
import * as React from 'react';
import type { Persona } from './steps';

export type FieldKind =
  | 'text' | 'email' | 'select' | 'multiselect' | 'textarea'
  | 'file' | 'number' | 'date' | 'radio' | 'checkbox';

export interface FieldDef {
  name:     string;
  label:    string;
  kind:     FieldKind;
  options?: { value: string; label: string }[];
  required?: boolean;
  hint?:     string;
  accept?:   string;  // for file
}

const ON_DEMAND  = { value: 'on_demand',  label: 'On-Demand' };
const EMERGENCY  = { value: 'emergency',  label: 'Emergency' };
const POOL       = { value: 'pool',       label: 'Anonymous Pool' };

export const FIELDS: Record<string, FieldDef[]> = {
  // ─── CLIENT ───────────────────────────────────────────
  'client/identity': [
    { name: 'first_name', label: 'First name',  kind: 'text',  required: true },
    { name: 'last_name',  label: 'Last name',   kind: 'text',  required: true },
    { name: 'role',       label: 'Function / Role', kind: 'text', required: true },
    { name: 'email',      label: 'Professional email', kind: 'email', required: true },
  ],
  'client/company': [
    { name: 'legal_name', label: 'Legal name',         kind: 'text', required: true },
    { name: 'siren',      label: 'SIREN / VAT number', kind: 'text', required: true },
    { name: 'website',    label: 'Website',            kind: 'text' },
    { name: 'size',       label: 'Headcount', kind: 'select', options: [
      { value: '1-10',    label: '1–10' },
      { value: '11-50',   label: '11–50' },
      { value: '51-200',  label: '51–200' },
      { value: '201-1000',label: '201–1 000' },
      { value: '1000+',   label: '1 000+' },
    ]},
  ],
  'client/intake': [
    { name: 'mode', label: 'Mission mode', kind: 'radio', required: true,
      options: [ON_DEMAND, EMERGENCY, POOL] },
    { name: 'context', label: 'Mission context', kind: 'textarea' },
  ],
  'client/upload': [
    { name: 'brief',  label: 'Brief / RFP', kind: 'file', accept: '.pdf,.docx', required: true },
    { name: 'notes',  label: 'Additional notes', kind: 'textarea' },
  ],
  'client/compliance': [
    { name: 'nis2',     label: 'NIS2 directive scope',         kind: 'checkbox' },
    { name: 'iso27001', label: 'ISO 27001 certified',          kind: 'checkbox' },
    { name: 'secnum',   label: 'SecNumCloud (ANSSI)',          kind: 'checkbox' },
    { name: 'soc2',     label: 'SOC 2 Type II',                kind: 'checkbox' },
    { name: 'hds',      label: 'HDS (health data)',            kind: 'checkbox' },
  ],
  'client/tech': [
    { name: 'stack', label: 'Required stack (comma-separated)', kind: 'multiselect',
      hint: 'e.g. Pentest, AppSec, Cloud GCP, SIEM Splunk',
      options: [
        { value: 'pentest',     label: 'Pentest' },
        { value: 'appsec',      label: 'AppSec' },
        { value: 'cloud_aws',   label: 'Cloud AWS' },
        { value: 'cloud_azure', label: 'Cloud Azure' },
        { value: 'cloud_gcp',   label: 'Cloud GCP' },
        { value: 'siem',        label: 'SIEM (Splunk/QRadar)' },
        { value: 'soc',         label: 'SOC analysis' },
        { value: 'iam',         label: 'IAM' },
        { value: 'devsecops',   label: 'DevSecOps' },
        { value: 'redteam',     label: 'Red Team' },
        { value: 'threatintel', label: 'Threat Intel' },
        { value: 'rgpd',        label: 'RGPD / DPO' },
      ] },
  ],
  'client/timeline': [
    { name: 'start_date',  label: 'Desired start date', kind: 'date',   required: true },
    { name: 'duration',    label: 'Duration (months)',  kind: 'number', required: true },
    { name: 'location',    label: 'Location', kind: 'select', required: true, options: [
      { value: 'remote',  label: 'Remote' },
      { value: 'onsite',  label: 'On-site' },
      { value: 'hybrid',  label: 'Hybrid' },
    ]},
    { name: 'country',     label: 'Country', kind: 'select', required: true, options: [
      { value: 'fr', label: 'France' },
      { value: 'uk', label: 'United Kingdom' },
      { value: 'de', label: 'Germany' },
      { value: 'ch', label: 'Switzerland' },
      { value: 'ae', label: 'UAE' },
      { value: 'us', label: 'United States' },
    ]},
  ],
  'client/output': [],   // rendered as candidate cards (handled in WizardShell)

  // ─── TALENT ───────────────────────────────────────────
  'talent/identity': [
    { name: 'first_name',  label: 'First name',     kind: 'text',  required: true },
    { name: 'last_name',   label: 'Last name',      kind: 'text',  required: true },
    { name: 'nationality', label: 'Nationality',    kind: 'text',  required: true },
    { name: 'id_doc',      label: 'ID document (passport / national ID)', kind: 'file', accept: '.pdf,.jpg,.png', required: true },
  ],
  'talent/cv': [
    { name: 'cv', label: 'CV (PDF)', kind: 'file', accept: '.pdf', required: true },
  ],
  'talent/skills': [
    { name: 'skills', label: 'Expertises', kind: 'multiselect', required: true, options: [
      { value: 'pentest',   label: 'Pentest' }, { value: 'appsec', label: 'AppSec' },
      { value: 'redteam',   label: 'Red Team' }, { value: 'soc', label: 'SOC' },
      { value: 'siem',      label: 'SIEM' }, { value: 'iam', label: 'IAM' },
      { value: 'cloud',     label: 'Cloud Security' }, { value: 'forensics', label: 'Forensics' },
      { value: 'rgpd',      label: 'RGPD / DPO' }, { value: 'crypto', label: 'Cryptography' },
    ]},
  ],
  'talent/certifs': [
    { name: 'oscp',  label: 'OSCP',          kind: 'checkbox' },
    { name: 'crest', label: 'CREST',         kind: 'checkbox' },
    { name: 'pasi',  label: 'ANSSI PASSI',   kind: 'checkbox' },
    { name: 'cissp', label: 'CISSP',         kind: 'checkbox' },
    { name: 'ceh',   label: 'CEH',           kind: 'checkbox' },
    { name: 'iso',   label: 'ISO 27001 LA',  kind: 'checkbox' },
    { name: 'extra', label: 'Other certifications', kind: 'textarea' },
  ],
  'talent/tests': [],  // rendered as test launcher cards
  'talent/availability': [
    { name: 'available_from', label: 'Available from', kind: 'date',   required: true },
    { name: 'tjm_eur',        label: 'Day rate (EUR)', kind: 'number', required: true },
    { name: 'mode',           label: 'Mode',           kind: 'select', required: true, options: [
      { value: 'remote', label: 'Remote' },
      { value: 'onsite', label: 'On-site' },
      { value: 'hybrid', label: 'Hybrid' },
    ]},
  ],
  'talent/prefs': [
    { name: 'sectors',   label: 'Preferred sectors', kind: 'multiselect', options: [
      { value: 'finance',   label: 'Finance / Banking' },
      { value: 'health',    label: 'Healthcare' },
      { value: 'energy',    label: 'Energy / OT' },
      { value: 'tech',      label: 'Tech / SaaS' },
      { value: 'public',    label: 'Public sector' },
      { value: 'defense',   label: 'Defense' },
      { value: 'industry',  label: 'Industry / Manufacturing' },
    ]},
    { name: 'countries', label: 'Geographical scope', kind: 'multiselect', options: [
      { value: 'fr', label: 'France' }, { value: 'uk', label: 'United Kingdom' },
      { value: 'de', label: 'Germany' }, { value: 'ch', label: 'Switzerland' },
      { value: 'ae', label: 'UAE' },     { value: 'us', label: 'United States' },
    ]},
  ],

  // ─── PARTNER ──────────────────────────────────────────
  'partner/company': [
    { name: 'legal_name', label: 'Company legal name', kind: 'text', required: true },
    { name: 'siren',      label: 'SIREN / VAT',        kind: 'text', required: true },
    { name: 'contact',    label: 'Primary contact',    kind: 'text', required: true },
    { name: 'email',      label: 'Contact email',      kind: 'email', required: true },
  ],
  'partner/pool': [
    { name: 'pool_csv',   label: 'Talent pool CSV',    kind: 'file', accept: '.csv',
      hint: 'Auto-prequalif without PII — fields hashed before scoring' },
    { name: 'manual',     label: 'Or list talents (one per line)', kind: 'textarea' },
  ],
  'partner/model': [
    { name: 'model', label: 'Engagement model', kind: 'radio', required: true, options: [
      { value: 'inbound',  label: 'Inbound — Xyqar sends missions to your pool' },
      { value: 'outbound', label: 'Outbound — your talents work on Xyqar missions' },
      { value: 'both',     label: 'Both' },
    ]},
  ],
  'partner/commissions': [
    { name: 'commission_pct', label: 'Commission rate (%)', kind: 'number', required: true,
      hint: 'Standard range 5–15%' },
    { name: 'iban',           label: 'IBAN for payments',   kind: 'text',  required: true },
  ],
  'partner/done': [],
};

export function getFields(persona: Persona, slug: string): FieldDef[] {
  return FIELDS[`${persona}/${slug}`] ?? [];
}

// ─── Field renderer (Wolf Dark) ──────────────────────────
export function FieldInput({
  field, value, onChange,
}: {
  field: FieldDef;
  value: any;
  onChange: (v: any) => void;
}) {
  const baseInput =
    'w-full rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm ' +
    'text-white placeholder-white/30 focus:outline-none focus:border-white/40 ' +
    'transition-colors';

  switch (field.kind) {
    case 'text':
    case 'email':
    case 'number':
    case 'date':
      return (
        <input
          type={field.kind}
          name={field.name}
          required={field.required}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={baseInput}
        />
      );
    case 'textarea':
      return (
        <textarea
          name={field.name}
          required={field.required}
          rows={4}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={baseInput + ' resize-y min-h-[100px]'}
        />
      );
    case 'select':
      return (
        <select
          name={field.name}
          required={field.required}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={baseInput}
        >
          <option value="" disabled>—</option>
          {field.options?.map((o) => (
            <option key={o.value} value={o.value} className="bg-black">
              {o.label}
            </option>
          ))}
        </select>
      );
    case 'radio':
      return (
        <div className="flex flex-col gap-3">
          {field.options?.map((o) => (
            <label key={o.value} className="flex items-center gap-3 cursor-pointer
              p-3 rounded-md bg-white/5 border border-white/10
              hover:border-white/30 transition-colors">
              <input
                type="radio"
                name={field.name}
                value={o.value}
                checked={value === o.value}
                onChange={() => onChange(o.value)}
                className="accent-white"
              />
              <span className="text-sm text-white">{o.label}</span>
            </label>
          ))}
        </div>
      );
    case 'checkbox':
      return (
        <label className="flex items-center gap-3 cursor-pointer
          p-3 rounded-md bg-white/5 border border-white/10
          hover:border-white/30 transition-colors">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="accent-white"
          />
          <span className="text-sm text-white">{field.label}</span>
        </label>
      );
    case 'multiselect': {
      const arr: string[] = Array.isArray(value) ? value : [];
      const toggle = (v: string) =>
        arr.includes(v) ? onChange(arr.filter((x) => x !== v)) : onChange([...arr, v]);
      return (
        <div className="flex flex-wrap gap-2">
          {field.options?.map((o) => {
            const active = arr.includes(o.value);
            return (
              <button
                type="button"
                key={o.value}
                onClick={() => toggle(o.value)}
                className={
                  'px-3 py-1.5 rounded-full text-xs uppercase tracking-wider transition-all ' +
                  (active
                    ? 'bg-white text-black border border-white'
                    : 'bg-transparent text-white/70 border border-white/20 hover:border-white/50')
                }
              >
                {o.label}
              </button>
            );
          })}
        </div>
      );
    }
    case 'file':
      return (
        <div className="rounded-md border border-dashed border-white/20
          bg-white/5 p-6 text-center cursor-pointer hover:border-white/40 transition-colors">
          <input
            type="file"
            accept={field.accept}
            required={field.required}
            onChange={(e) => onChange(e.target.files?.[0]?.name ?? '')}
            className="block w-full text-sm text-white/70 file:mr-3 file:py-2 file:px-4
              file:rounded-md file:border file:border-white/20 file:text-xs
              file:bg-white file:text-black file:cursor-pointer file:uppercase
              file:tracking-wider"
          />
          {value && <p className="mt-2 text-xs text-white/50">{value}</p>}
        </div>
      );
  }
}
